/**
 * One game, in full: what it is, how it plays, and the way in.
 *
 * The same page whether you own it or not — a person deciding whether to buy
 * wants exactly what a person deciding whether to play wants. Only the button
 * at the bottom differs.
 *
 * ## It shows the game's picture, and that is the point of the page
 *
 * The library draws every game at 46px in a grid of nine. This is the one
 * screen showing a single game, so it is drawn at the size it was always meant
 * for — same art, same gradient, same tone, just given room. It is also the
 * only thing here that says *which* game you are reading about without being
 * read.
 *
 * Which is why it took the hero art rather than keeping the fallback mark
 * (X4). A game whose picture exists was showing the picture on the card and a
 * crown or a spade on the page one tap away: two identities for one game, on
 * consecutive screens. The mark is what stands in until art exists, not an
 * alternative to it, so both screens make the same test.
 */

import { Link, useParams } from 'react-router-dom';
import { useCatalog } from '../use-catalog.js';
import { Icon } from '../render/Icon.js';
import { playerCount } from '../render/players.js';
import { gameArt, heroArt } from '../render/game-art.js';
import { ActionBar, BackLink } from './ActionBar.js';

export function GameDetail() {
  const { gameId } = useParams<{ gameId: string }>();
  const { catalog } = useCatalog();

  if (!catalog) return <div className="splash">Dealing…</div>;

  const game = catalog.games.find((candidate) => candidate.id === gameId);
  if (!game) {
    return (
      <div className="page narrow felt-page detail-page">
        <DetailBar title="No such game" />
        <p className="page-lede">
          That game is not in the catalog. It may have been renamed.
        </p>
        <ActionBar>
          <BackLink to="/library" label="Your games" />
        </ActionBar>
      </div>
    );
  }

  const players = playerCount(game.minPlayers, game.maxPlayers);

  /*
   * Owning a game and being able to host a big table are different things.
   * `maxSeats` is what this account may actually open, so a player whose tier
   * caps below the game's maximum is told here rather than at the refusal.
   */
  const cappedBelowGame = game.maxSeats > 0 && game.maxSeats < game.maxPlayers;

  const art = gameArt(game.id, game.family);

  /* The same call the library card makes, so the two cannot disagree about
     whether this game has a picture. */
  const hero = heroArt(game.id);

  return (
    <div className="page narrow felt-page detail-page">
      <DetailBar title={game.name} />

      {/*
        A cap worth knowing about before you open a table, not after.

        It used to sit under the button; the button is at the foot of the
        screen now and this is a fact about the game, so it reads with the
        game rather than following a control that has moved away from it.
      */}
      {game.owned && cappedBelowGame ? (
        <p className="field-help">
          You can seat {game.maxSeats} at a table. Bigger Tables raises that to {game.maxPlayers}.
        </p>
      ) : null}

      {/*
        The mark, the facts, and the pitch, on one surface.

        The facts were a run-on line - "4 players · about 30 minutes" - and are
        chips here for the reason E3 gave on the cards: chips wrap between
        facts rather than through them. Using the same chips also means a fact
        looks the same wherever somebody meets it.
      */}
      <section className="detail-hero form-card">
        {/*
         * The art, where the library card already shows it.
         *
         * These were two identities for one game, one screen apart: the card
         * in the library had the painting and the page you reached by tapping
         * it had the fallback mark - a crown for Klondike, a spade for
         * Pyramid. The mark was never an alternative to the art, it is what
         * stands in until the art exists, and the same rule decides here as
         * decides there.
         *
         * Full width rather than the card's small square, because this is the
         * one screen showing a single game and the picture is drawn 2:1 to be
         * looked at.
         */}
        {hero === null ? (
          <span className={`detail-mark tone-${art.tone}`} aria-hidden="true">
            <Icon name={art.icon} />
          </span>
        ) : (
          <span className="detail-art">
            <img src={hero} alt="" />
          </span>
        )}

        <span className="detail-chips">
          <span className="game-chip">{players}</span>
          <span className="game-chip">{game.playTimeMinutes} min</span>
          {game.difficulty ? (
            <span className={`game-chip difficulty ${game.difficulty.toLowerCase()}`}>
              {game.difficulty} to learn
            </span>
          ) : null}
          {game.free ? <span className="game-chip free">Free</span> : null}
        </span>

        <p className="game-description">{game.description}</p>
      </section>

      <h2 className="section-title">How it plays</h2>
      <div className="form-card">
        <ul className="rules-list">
          {game.howToPlay.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      {/*
        Both ways out of this screen, pinned where a thumb is.

        The action used to sit above the rules, on the reasoning that the
        page's longest section should not come between a player and the table.
        The bar answers that better than moving it around did: it is on screen
        the whole way down the rules, so there is no point in the scroll where
        the way to play is out of reach.

        A game you do not own keeps the store as its action - that IS the way
        on from here. What went is the Store pill that sat in the corner of
        every one of these pages, owned or not, which was a second destination
        competing with the page's own purpose.
      */}
      <ActionBar>
        <BackLink to="/library" label="Your games" />
        {game.owned ? (
          <Link className="cta cta-primary" to={`/host/${game.id}`}>
            <Icon name="spade" className="cta-mark" />
            <span className="cta-label">Start a game</span>
            <Icon name="chevron-right" />
          </Link>
        ) : (
          <Link className="cta cta-primary" to="/store">
            <Icon name="join-group" className="cta-mark" />
            <span className="cta-label">Get it in the store</span>
            <Icon name="chevron-right" />
          </Link>
        )}
      </ActionBar>
    </div>
  );
}

/**
 * The game's name, and nothing else.
 *
 * This carried a Back on the left and a Store pill on the right. Both have
 * gone: back is half of the bar at the foot of the screen, where it is a
 * thumb's reach from the action it belongs beside rather than at the top of a
 * page you have scrolled away from — and the store was a way *off* this page
 * sitting in the corner of a page about one game, offered just as readily to
 * somebody who already owns it.
 *
 * The title keeps `.page-bar`'s three-column grid with both side slots empty,
 * which is what holds it in the true middle of the header. Centring the
 * contents of a flex run centres the run, which is not the same thing.
 */
function DetailBar({ title }: { title: string }) {
  return (
    <header className="page-bar">
      <div className="page-bar-action" />
      <h1 className="page-title">{title}</h1>
      <div className="page-bar-action" />
    </header>
  );
}
